import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { RankingEntry } from '@kaeliux/shared';
import { useAuthStore } from '../store/authStore';
import { cn } from '../utils/cn';
import { formatFullNumber } from '../utils/formatters';
import { getPlayerRank, getPlayerRankingEntry, getTopRankings, TOP_RANKINGS_LIMIT } from '../services/ranking.service';
import { RACE_LOGOS } from '../utils/raceAssets';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Table, Th, Td, TABLE_ROW_BORDER_CLASSES, TABLE_ROW_HIGHLIGHT_CLASSES } from '../components/ui/Table';

const PAGE_SIZE = 20;

type SortKey = 'rank' | 'displayName' | 'alliance' | 'total' | 'battlePoints' | 'resourcePoints';
type SortDir = 'asc' | 'desc';

export interface RankedEntry extends RankingEntry {
    /** Position in the true (total-points-descending) leaderboard order — stays fixed regardless of the table's current sort column. */
    rank: number;
}

const COLUMNS: { key: SortKey; label: string }[] = [
    { key: 'rank', label: 'Rank' },
    { key: 'displayName', label: 'Player' },
    { key: 'alliance', label: 'Alliance' },
    { key: 'total', label: 'Total Points' },
    { key: 'battlePoints', label: 'Battle Points' },
    { key: 'resourcePoints', label: 'Resource Points' },
];

function sortValue(entry: RankedEntry, key: SortKey): number | string {
    switch (key) {
        case 'rank': return entry.rank;
        case 'displayName': return entry.displayName.toLowerCase();
        // Unaffiliated players sort last ascending rather than first: an
        // empty string would otherwise bubble every alliance-less commander
        // to the top of an "Alliance ▲" sort, which reads as a bug.
        case 'alliance': return entry.allianceName?.toLowerCase() ?? '￿';
        case 'total': return entry.total;
        case 'battlePoints': return entry.battlePoints;
        case 'resourcePoints': return entry.resourcePoints;
    }
}

interface RankingRowProps {
    entry: RankedEntry;
    isMe: boolean;
}

export function RankingRow({ entry, isMe }: RankingRowProps) {
    return (
        <tr className={cn(TABLE_ROW_BORDER_CLASSES, isMe && TABLE_ROW_HIGHLIGHT_CLASSES)}>
            <Td className="text-content-muted">#{entry.rank}</Td>
            <Td>
                <Link to={`/player/${entry.userId}`} className="inline-flex items-center gap-2 hover:text-content-primary">
                    <img
                        src={RACE_LOGOS[entry.race]}
                        alt={entry.displayName}
                        title={entry.displayName}
                        className="w-8 h-8 rounded-full object-cover shrink-0"
                    />
                    <span className="text-content-primary">{entry.displayName}</span>
                </Link>
            </Td>
            <Td>
                {entry.allianceId ? (
                    <Link
                        to={`/alliance/${entry.allianceId}`}
                        className="text-content-secondary hover:text-content-primary hover:underline"
                        title={entry.allianceName}
                    >
                        <span className="text-content-muted">[{entry.allianceTag}]</span> {entry.allianceName}
                    </Link>
                ) : (
                    <span className="text-content-muted">—</span>
                )}
            </Td>
            <Td className="font-semibold text-content-primary">{formatFullNumber(entry.total)}</Td>
            <Td className="text-content-muted">{formatFullNumber(entry.battlePoints)}</Td>
            <Td className="text-content-muted">{formatFullNumber(entry.resourcePoints)}</Td>
        </tr>
    );
}

export default function RankingPage() {
    const { profile } = useAuthStore();
    const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'rank', dir: 'asc' });
    const [page, setPage] = useState(0);

    const topQuery = useQuery({
        queryKey: ['rankings', 'top'],
        queryFn: () => getTopRankings(TOP_RANKINGS_LIMIT),
        staleTime: 30_000,
    });

    const myEntryQuery = useQuery({
        queryKey: ['rankings', 'me', profile?.id],
        queryFn: () => getPlayerRankingEntry(profile!.id),
        enabled: !!profile,
        staleTime: 30_000,
    });

    const myRankQuery = useQuery({
        queryKey: ['rankings', 'myRank', myEntryQuery.data?.total],
        queryFn: () => getPlayerRank(myEntryQuery.data!.total),
        enabled: myEntryQuery.data != null,
        staleTime: 30_000,
    });

    const ranked: RankedEntry[] = useMemo(
        () => (topQuery.data ?? []).map((entry, index) => ({ ...entry, rank: index + 1 })),
        [topQuery.data],
    );

    const isInTopList = !!profile && ranked.some((entry) => entry.userId === profile.id);
    const myPinnedEntry: RankedEntry | null =
        !isInTopList && myEntryQuery.data && myRankQuery.data
            ? { ...myEntryQuery.data, rank: myRankQuery.data }
            : null;

    const sorted = useMemo(() => {
        const dirMultiplier = sort.dir === 'asc' ? 1 : -1;
        return [...ranked].sort((a, b) => {
            const av = sortValue(a, sort.key);
            const bv = sortValue(b, sort.key);
            if (av < bv) return -1 * dirMultiplier;
            if (av > bv) return 1 * dirMultiplier;
            return 0;
        });
    }, [ranked, sort]);

    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageRows = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

    function toggleSort(key: SortKey) {
        setPage(0);
        setSort((current) =>
            current.key === key
                ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: key === 'rank' || key === 'displayName' ? 'asc' : 'desc' },
        );
    }

    return (
        <main className="max-w-5xl mx-auto p-4 flex flex-col gap-4">
            <Card>
                <h2 className="text-display text-content-primary">Ranking Board</h2>
            </Card>

            <Card>
                {topQuery.isPending ? (
                    <p className="text-content-muted animate-pulse">Loading rankings…</p>
                ) : topQuery.isError ? (
                    <p className="text-status-danger">Failed to load the ranking board.</p>
                ) : (
                    <>
                        <Table>
                            <thead>
                                <tr>
                                    {COLUMNS.map((col) => (
                                        <Th key={col.key}>
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 hover:text-content-primary"
                                                onClick={() => toggleSort(col.key)}
                                            >
                                                {col.label}
                                                {sort.key === col.key && (
                                                    <span aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                                                )}
                                            </button>
                                        </Th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((entry) => (
                                    <RankingRow key={entry.userId} entry={entry} isMe={entry.userId === profile?.id} />
                                ))}
                                {myPinnedEntry && (
                                    <>
                                        <tr aria-hidden="true">
                                            <Td colSpan={COLUMNS.length} className="text-center text-content-muted">⋯</Td>
                                        </tr>
                                        <RankingRow entry={myPinnedEntry} isMe />
                                    </>
                                )}
                            </tbody>
                        </Table>
                        {pageRows.length === 0 && !myPinnedEntry && (
                            <p className="text-content-muted">No ranked players yet.</p>
                        )}
                    </>
                )}
            </Card>

            {sorted.length > PAGE_SIZE && (
                <div className="flex items-center justify-center gap-3 text-body">
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                        Previous
                    </Button>
                    <span className="text-content-muted">Page {page + 1} of {pageCount}</span>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={page >= pageCount - 1}
                        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    >
                        Next
                    </Button>
                </div>
            )}
        </main>
    );
}
